import { Theme, useTheme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { createSxStyles } from "../common/createSxStyles";

const brandPink = "#E127AA";
const brandYellow = "#FAC219";
const brandGradient = `linear-gradient(180deg, ${brandPink} 0%, ${brandYellow} 100%)`;
const userMessageGradient = "linear-gradient(90deg, #FB923C 0%, #F472B6 100%)";
const headerIconColor = "#002C75";
const inputBorderColor = "#E2E2E2";
const placeholderColor = "#767676";

export const aiChatResizeHandleColor = brandPink;

/**
 * The chat's 2px edge is a brand gradient, which a border cannot paint, so it is drawn as a background
 * image clipped to the border box. The rail shell takes it as-is.
 */
export function useAiChatBorderTreatment(): SystemStyleObject<Theme> {
    const theme = useTheme();

    return {
        border: "2px solid transparent",
        backgroundImage: `linear-gradient(${theme.customPalette.white}, ${theme.customPalette.white}), ${brandGradient}`,
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",
    };
}

export const useAiChatStyles = (isExpanded: boolean = false) => {
    const theme = useTheme();

    return createSxStyles({
        sidebarColumn: {
            width: 360,
            minWidth: 360,
            height: "100%",
            borderRight: `1px solid ${theme.palette.divider}`,
        },
        chatColumn: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            height: "100%",
        },
        header: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: theme.spacing(1, 1.5),
            borderBottom: isExpanded ? `1px solid ${theme.palette.divider}` : `1px solid ${theme.palette.grey[100]}`,
            minHeight: 44,
        },
        headerTitle: {
            fontWeight: 300,
            fontSize: 16,
            color: brandPink,
            fontFamily: "Poppins, Open Sans, sans-serif",
        },
        headerActions: {
            display: "flex",
            alignItems: "center",
            gap: 0.5,
        },
        headerIconButton: {
            color: headerIconColor,
            padding: theme.spacing(0.75),
            "& .MuiSvgIcon-root": {
                fill: headerIconColor,
            },
            "&:hover": {
                backgroundColor: theme.palette.grey[100],
            },
        },
        messageList: {
            flex: 1,
            overflowY: "auto",
            padding: theme.spacing(2),
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1.5),
            ...(isExpanded && {
                width: "100%",
                maxWidth: 900,
                marginLeft: "auto",
                marginRight: "auto",
                paddingLeft: theme.spacing(4),
                paddingRight: theme.spacing(4),
            }),
        },
        welcomeContainer: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: theme.spacing(3),
            textAlign: "center",
            gap: theme.spacing(1),
            ...(isExpanded && {
                width: "100%",
                maxWidth: 900,
                marginLeft: "auto",
                marginRight: "auto",
                paddingLeft: theme.spacing(4),
                paddingRight: theme.spacing(4),
            }),
        },
        welcomeMascot: {
            display: "flex",
            justifyContent: "center",
            marginBottom: theme.spacing(0.5),
        },
        welcomeBrand: {
            display: "flex",
            justifyContent: "center",
            marginBottom: theme.spacing(0.25),
            marginTop: theme.spacing(0.5),
        },
        welcomeTitle: {
            fontSize: 22,
            fontWeight: 300,
            color: brandPink,
            fontFamily: "Poppins, Open Sans, sans-serif",
            lineHeight: 1.2,
            marginTop: theme.spacing(0.25),
            marginBottom: theme.spacing(2),
        },
        welcomeDescription: {
            fontSize: 13,
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            mb: 2,
        },
        suggestionChip: {
            fontSize: 12,
            padding: theme.spacing(1, 2),
            borderRadius: theme.spacing(2),
            border: `1px solid ${theme.palette.divider}`,
            cursor: "pointer",
            backgroundColor: "transparent",
            color: theme.palette.text.secondary,
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.spacing(1),
            "&:hover": {
                borderColor: brandPink,
                color: brandPink,
                backgroundColor: "#FCE4F3",
            },
        },
        userMessage: {
            alignSelf: "flex-end",
            maxWidth: "85%",
            padding: theme.spacing(1.25, 2),
            borderRadius: theme.spacing(2, 0.5, 2, 2),
            background: userMessageGradient,
            color: "white",
            fontSize: 13,
            lineHeight: 1.5,
            wordBreak: "break-word",
        },
        userAttachmentBubble: {
            alignSelf: "flex-end",
            maxWidth: "85%",
            padding: theme.spacing(1, 2),
            borderRadius: theme.spacing(2, 0.5, 2, 2),
            background: userMessageGradient,
            color: "white",
            display: "flex",
            flexWrap: "wrap",
            gap: theme.spacing(0.5),
        },
        assistantMessage: {
            alignSelf: "flex-start",
            maxWidth: "100%",
            width: "100%",
            padding: theme.spacing(1.25, 2),
            borderRadius: theme.spacing(0.5, 2, 2, 2),
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.text.primary,
            fontSize: 13,
            lineHeight: 1.5,
            wordBreak: "break-word",
            "& p": { margin: theme.spacing(0.5, 0) },
            "& p:first-of-type": { marginTop: 0 },
            "& p:last-of-type": { marginBottom: 0 },
            "& ul, & ol": { margin: theme.spacing(0.5, 0), paddingLeft: theme.spacing(2.5) },
            "& li": { marginBottom: theme.spacing(0.25) },
            "& code": {
                backgroundColor: theme.palette.grey[200],
                borderRadius: theme.spacing(0.5),
                padding: theme.spacing(0.25, 0.5),
                fontSize: 12,
            },
            "& pre": {
                backgroundColor: theme.palette.grey[200],
                borderRadius: theme.spacing(1),
                padding: theme.spacing(1),
                overflowX: "auto",
                margin: theme.spacing(0.5, 0),
                "& code": { backgroundColor: "transparent", padding: 0 },
            },
            // Distinct sizes per heading level so the model's `###` / `##` / `#` structure is
            // visible in the rendered output. Without this all headings collapse to the same
            // weight/size as `**bold paragraph text**` and the hierarchy disappears.
            "& h1": { fontSize: 17, fontWeight: 600, margin: theme.spacing(1.5, 0, 0.5, 0) },
            "& h2": { fontSize: 16, fontWeight: 600, margin: theme.spacing(1.25, 0, 0.5, 0) },
            "& h3": { fontSize: 15, fontWeight: 600, margin: theme.spacing(1, 0, 0.5, 0) },
            "& h4": { fontSize: 14, fontWeight: 600, margin: theme.spacing(1, 0, 0.5, 0) },
            "& h5, & h6": { fontSize: 13, fontWeight: 600, margin: theme.spacing(1, 0, 0.5, 0) },
            "& strong": { fontWeight: 600 },
            "& a": { color: theme.palette.primary.main, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
            "& .table-wrapper": { overflowX: "auto", margin: theme.spacing(0.5, 0) },
            "& table": { borderCollapse: "collapse", fontSize: 12, minWidth: "100%", whiteSpace: "nowrap" },
            "& th, & td": { border: `1px solid ${theme.palette.divider}`, padding: theme.spacing(0.5, 1), textAlign: "left" },
            "& th": { backgroundColor: theme.palette.grey[200], fontWeight: 600 },
        },
        inputArea: {
            padding: theme.spacing(1.5, 2, 1, 2),
            borderTop: "none",
            ...(isExpanded && {
                width: "100%",
                maxWidth: 900,
                marginLeft: "auto",
                marginRight: "auto",
                paddingLeft: theme.spacing(4),
                paddingRight: theme.spacing(4),
            }),
        },
        inputWrapper: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
            padding: theme.spacing(1.5),
            borderRadius: theme.spacing(2),
            border: `1px solid ${inputBorderColor}`,
            backgroundColor: theme.customPalette.white,
            boxShadow: "0 4px 4px rgba(211, 211, 211, 0.1)",
            "&:focus-within": {
                borderColor: brandPink,
            },
        },
        inputControls: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: theme.spacing(1),
        },
        textField: {
            flex: 1,
            "& .MuiInputBase-root": {
                padding: 0,
                fontSize: 14,
                lineHeight: 1.5,
            },
            "& .MuiInputBase-input": {
                padding: 0,
                "&::placeholder": {
                    color: placeholderColor,
                    opacity: 1,
                },
            },
            "& fieldset": {
                border: "none",
            },
        },
        sendButton: {
            minWidth: 32,
            width: 32,
            height: 32,
            padding: 0,
            backgroundColor: "transparent",
            "& svg": {
                width: 30,
                height: 30,
                transition: "filter 120ms ease",
            },
            "&:hover": {
                backgroundColor: "transparent",
                "& svg": {
                    filter: "brightness(0.78)",
                },
            },
            "&.Mui-disabled": {
                opacity: 0.4,
            },
        },
        disclaimer: {
            fontSize: 10,
            color: theme.palette.grey[500],
            textAlign: "center",
            marginTop: theme.spacing(1),
            paddingBottom: theme.spacing(0.5),
        },
        resultsTruncatedNotice: {
            marginTop: theme.spacing(1),
            paddingTop: theme.spacing(0.75),
            borderTop: `1px dashed ${theme.palette.divider}`,
            fontSize: 11,
            fontStyle: "italic",
            color: theme.palette.text.secondary,
            lineHeight: 1.4,
        },
        toolCallText: {
            fontSize: 12,
            fontStyle: "italic",
            background: `linear-gradient(90deg, ${theme.palette.text.secondary} 0%, ${theme.palette.text.disabled} 50%, ${theme.palette.text.secondary} 100%)`,
            backgroundSize: "200% 100%",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            animation: "shimmer 2s ease-in-out infinite",
            "@keyframes shimmer": {
                "0%": { backgroundPosition: "200% 0" },
                "100%": { backgroundPosition: "-200% 0" },
            },
        },
        assistantMessageContainer: {
            alignSelf: "flex-start",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
        },
        feedbackRow: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.25),
        },
        feedbackIconButton: {
            padding: theme.spacing(0.5),
            color: theme.palette.text.secondary,
            "&:hover": {
                backgroundColor: theme.palette.grey[100],
            },
        },
        feedbackIconButtonSelected: {
            padding: theme.spacing(0.5),
            color: theme.palette.primary.main,
            "&:hover": {
                backgroundColor: theme.palette.grey[100],
            },
        },
        activityPanel: {
            paddingLeft: theme.spacing(0.5),
        },
        activityHeader: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.5),
            padding: 0,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: theme.palette.text.secondary,
            "&:hover": {
                color: theme.palette.text.primary,
            },
        },
        activityHeaderText: {
            fontSize: 12,
            color: "inherit",
        },
        activityToggleIcon: {
            fontSize: 16,
            color: "inherit",
        },
        activityBody: {
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1.5),
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
            paddingLeft: "24px",
            "&::before": {
                content: '""',
                position: "absolute",
                left: "11px",
                top: "4px",
                bottom: "4px",
                width: "1px",
                backgroundColor: theme.palette.grey[300],
            },
        },
        activityRow: {
            position: "relative",
            display: "flex",
        },
        activityRowIcon: {
            position: "absolute",
            left: "-21px",
            top: 0,
            width: 18,
            height: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.customPalette.white,
            color: theme.palette.text.secondary,
            "& svg": { fontSize: 14 },
        },
        activityRowDot: {
            position: "absolute",
            left: "-21px",
            top: 0,
            width: 18,
            height: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.customPalette.white,
            "&::after": {
                content: '""',
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: theme.palette.text.secondary,
            },
        },
        activityThinking: {
            fontSize: 13,
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            "& p": { margin: theme.spacing(0.5, 0) },
            "& p:first-of-type": { marginTop: 0 },
            "& p:last-of-type": { marginBottom: 0 },
            "& ul, & ol": { margin: theme.spacing(0.5, 0), paddingLeft: theme.spacing(2.5) },
            "& li": { marginBottom: theme.spacing(0.25) },
            "& h1, & h2, & h3, & h4, & h5, & h6": {
                fontSize: 13,
                fontWeight: 600,
                margin: theme.spacing(0.75, 0, 0.25, 0),
            },
            "& strong": { fontWeight: 600 },
            "& code": {
                fontSize: 11,
                backgroundColor: theme.palette.grey[200],
                borderRadius: theme.spacing(0.5),
                padding: theme.spacing(0.1, 0.4),
            },
            "& pre": {
                backgroundColor: theme.palette.grey[200],
                borderRadius: theme.spacing(0.5),
                padding: theme.spacing(0.75),
                overflowX: "auto",
                margin: theme.spacing(0.5, 0),
                "& code": { backgroundColor: "transparent", padding: 0 },
            },
            "& blockquote": {
                borderLeft: `2px solid ${theme.palette.divider}`,
                paddingLeft: theme.spacing(1),
                margin: theme.spacing(0.5, 0),
                color: theme.palette.text.disabled,
            },
            "& a": { color: theme.palette.primary.main, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
            "& table": { borderCollapse: "collapse", fontSize: 10.5, margin: theme.spacing(0.5, 0) },
            "& th, & td": { border: `1px solid ${theme.palette.divider}`, padding: theme.spacing(0.25, 0.75), textAlign: "left" },
        },
        activityToolCall: {
            fontSize: 13,
            fontWeight: 500,
            color: theme.palette.text.primary,
            lineHeight: "18px",
        },
        activityToolCallActive: {
            fontSize: 13,
            fontWeight: 500,
            lineHeight: "18px",
            background: `linear-gradient(90deg, ${theme.palette.text.primary} 0%, ${theme.palette.text.disabled} 50%, ${theme.palette.text.primary} 100%)`,
            backgroundSize: "200% 100%",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            animation: "aiChatActivityShimmer 2s ease-in-out infinite",
            "@keyframes aiChatActivityShimmer": {
                "0%": { backgroundPosition: "200% 0" },
                "100%": { backgroundPosition: "-200% 0" },
            },
        },
        activityThinkingPlain: {
            whiteSpace: "pre-wrap",
        },
        activityToolCallLink: {
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 500,
            lineHeight: "18px",
            marginLeft: theme.spacing(0.5),
            color: theme.palette.primary.main,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": { textDecoration: "none" },
        },
        typingIndicator: {
            display: "flex",
            gap: theme.spacing(0.5),
            alignSelf: "flex-start",
            padding: theme.spacing(1.5, 2),
            borderRadius: theme.spacing(2, 2, 2, 0.5),
            backgroundColor: theme.palette.grey[100],
            "& span": {
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: theme.palette.text.disabled,
                animation: "bounce 1.4s infinite ease-in-out",
            },
            "& span:nth-of-type(1)": {
                animationDelay: "0s",
            },
            "& span:nth-of-type(2)": {
                animationDelay: "0.2s",
            },
            "& span:nth-of-type(3)": {
                animationDelay: "0.4s",
            },
            "@keyframes bounce": {
                "0%, 80%, 100%": {
                    transform: "scale(0)",
                },
                "40%": {
                    transform: "scale(1)",
                },
            },
        },
    });
};
